import { error as logError } from '@services/log';
import { QueryKey, useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useProductInventory } from './useProductInventory';

interface UseProductInventoryQueryProps {
  queryKey?: QueryKey;
}
/**
 * Custom React Query hook for fetching product inventory data.
 *
 * @template T - The expected type of the response payload
 * @param endpoint - The API endpoint (relative to the base URL)
 * @param queryParams - Query parameters to append to the URL
 * @param options - Optional query configuration including custom queryKey
 * @param options.queryKey - Additional query key segments to prepend
 * @returns UseQueryResult containing the data and query state
 *
 * @example
 * const { data, isLoading } = useProductInventoryQuery<PartCategoryTree>(
 *   '/products/123/part-categories',
 *   { version: 'current', categoryFields: 'salesName,prices' },
 *   { queryKey: ['partTree', 'account123'] }
 * );
 */
export const useProductInventoryQuery = <T>(
  endpoint: string,
  queryParams: Record<string, string | string[]>,
  options?: UseQueryOptions<T> & UseProductInventoryQueryProps
): UseQueryResult<T> => {
  const { queryKey = [], ...useQueryOptions } = options ?? {};
  const allQueryKeys = [...queryKey, endpoint, queryParams];
  const { getService } = useProductInventory();

  return useQuery<T>(
    allQueryKeys,
    async () => {
      try {
        const service = await getService();
        const response = await service.get(endpoint, { params: queryParams });

        return (response.data.payload ?? response.data) as T;
      } catch (error) {
        logError(error as Error);
        throw error;
      }
    },
    useQueryOptions
  );
};
